import React, { ReactNode, useState, useRef } from 'react';
import produce from "immer";
import { Overlay, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Devices.scss';
import { ErrorMessage, Loader, Page, PageHeader } from 'components';
import { ErrorType, DeviceLoadState, ModalState, UserRole } from 'enums';
import { IDevice, IDeviceQuery } from 'interfaces';
import { DevicesItem } from './DevicesItem';
import { DeviceModal } from './DeviceModal';
import { DeviceService,  ManufacturerService,  parseAxiosError, UserService } from 'services';
import { Button, ButtonGroup, Modal, ModalBody, ModalFooter, ModalHeader, TextField } from '@cfa/react-components';
import { DeleteModal } from './DeleteModal';

interface IDevicesPageState {
    // device list
    deviceLoadStatus: DeviceLoadState;
    deviceLoadError?: string;
    // select
    selectedDevices?: IDevice[];
    selectedDevice?: IDevice;
    // device modal
    selectedState?: ModalState;
    showDeviceModal?: boolean;
    // delete modal
    showDeleteModal?: boolean;
}

const pageSize = 100;
export function Devices() {

    const [pageState, setPageState] = useState<IDevicesPageState>({
        deviceLoadStatus: DeviceLoadState.Initial,
        selectedDevices: [],
        selectedState: ModalState.View,
    });
    const { deviceLoadStatus, deviceLoadError, selectedDevices, selectedDevice, selectedState, showDeviceModal, showDeleteModal } = pageState;
    const [devices, setDevices] = useState<IDevice[]>([]);

    // refresh list to reflect changes
    const refresh = () => {
        setDevices(DeviceService.allDevices);
    }

    // search button and popover
    const searchButtonRef = useRef(null);
    const [searchOpen, setSearchOpen] = useState(false);

    // sorters
    const [activeSorter, setActiveSorter] = useState("lastSeen");
    const [descending, setDescending] = useState(true);

    // form search values
    // TODO - use actual form and map to query
    const [location, setLocation] = useState('');
    const [manufacturer, setManufacturer] = useState('');
    const [modelNumber, setModelNumber] = useState('');
    const [serialNumber, setSerialNumber] = useState('');
    const [thingID, setThingID] = useState('');

    // select all
    const [allSelected, setAllSelected] = useState(false);

    // Search
    const onChangeManufacturer = (e) => {
        setManufacturer(e.target.value);
    }

    const onChangeModelNumber = (e) => {
        setModelNumber(e.target.value);
    }

    const onChangeLocation = (e) => {
        setLocation(e.target.value);
    }

    const onChangeSerialNumber = (e) => {
        setSerialNumber(e.target.value);
    }

    const onChangeThingID = (e) => {
        setThingID(e.target.value);
    }

    const onKeypress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSubmitSearchValues();
        }
    }

    const onSubmitSearchValues = () => {

        if (manufacturer == "" && location == "" && modelNumber == "" && serialNumber == "" && thingID == "") return;

        setSearchOpen(false);
        setAllSelected(false);

        setDevices([]);
        setPageState(
            produce((draft) => {
                draft.deviceLoadStatus = DeviceLoadState.Loading;
                draft.selectedDevices = [];
            })
        );

        let query: IDeviceQuery = {
            thingID: thingID,
            manufacturerName: manufacturer,
            locationNumber: location,
            serialNumber: serialNumber,
            modelNumber: modelNumber,
            offset: 0,
            limit: pageSize, 
            sort: getDataApiSorter(activeSorter, descending),
        }
   
        DeviceService.getDevices(query).then((devs) => {
            doSuccess(devs);
        }).catch((err) => {
            // handle 404 composite query
            if (err.response.status == 404) {
                doSuccess([]);
                return;
            }
            setPageState(
                produce((draft) => {
                    draft.deviceLoadStatus = DeviceLoadState.Error;
                    draft.deviceLoadError = parseAxiosError(err);
                })
            );
        });
    };

    const doSuccess = (devs) => {
        let state = DeviceLoadState.Success;
        setDevices(devs);
        setPageState(
            produce((draft) => {
                draft.deviceLoadStatus = state; 
            })
        );
    };

    function getDataApiSorter(sorter: string, descending: boolean): string {
        if(descending) {
            sorter = sorter + "Desc"
        }
        return sorter
    }

    // Sorts a function using the passed sorter. This requires refetching all devices from data api.
    // If all devices are already in memory the sortFunc is used instead of a call to data api
    const sortBy = (newSorter: string, sortFunc:  (a: IDevice, b: IDevice) => number) => {
        let newDescending =  descending
        if (activeSorter == newSorter) {
            newDescending = !newDescending
        } else {
            newDescending = true
        }
        setActiveSorter(newSorter);
        setDescending(newDescending)

        if (activeSorter != newSorter) {
            setDevices(devices.slice().sort(sortFunc));
        } else {
            setDevices(devices.slice().reverse());
        }
    }

    const collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
    const sortDevicesByManufacturer = () => sortBy("manufacturer", (a, b) => collator.compare(a.manufacturer.name + a.modelNumber, b.manufacturer.name + b.modelNumber));
    const sortDevicesByLastSeen = () => sortBy("lastSeen", (a, b) => b.lastSeen.getTime() - a.lastSeen.getTime());

    // Device select
    const isSelected = input => selectedDevices.some(({id}) => id == input);
    
    const onToggleSelectAll = () => {
        let newSelectedDevices;
        
        if (allSelected) newSelectedDevices = [];
        else newSelectedDevices = devices.slice();

        setAllSelected(!allSelected);
        setPageState(produce(draft => {draft.selectedDevices = newSelectedDevices}))
    }

    const onSelectDevice = (d: IDevice) => {
        let newSelectedDevices = selectedDevices.slice();

        if (isSelected(d.id)) {
            const deviceIndex = newSelectedDevices.findIndex(({id}) => id == d.id);
            newSelectedDevices.splice(deviceIndex, 1);
        }
        else newSelectedDevices.push(d);

        if (newSelectedDevices.length == devices.length) setAllSelected(true);
        else setAllSelected(false)

        setPageState(produce(draft => {draft.selectedDevices = newSelectedDevices}))
    }

    // Device modal
    const openDeviceModal = (d: IDevice, s: ModalState) => {
        setPageState(produce(draft => {
            draft.selectedDevice = d;
            draft.selectedState = s;
            draft.showDeviceModal = true;
        }))
    }

    const closeDeviceModal = () => {
        setPageState(produce(draft => {
            draft.selectedState = ModalState.View;
            draft.showDeviceModal = false;
        }))
    }

    // Delete
    const openDeleteModal = (devs: IDevice[]) => {
        setPageState(produce(draft => {
            draft.selectedDevices = devs;
            draft.showDeleteModal = true;
        }))
    }

    const closeDeleteModal = () => {
        setPageState(produce(draft => {
            draft.showDeleteModal = false;
            draft.selectedDevices = [];
        }))
    }

    const clearDevices = () => {
        setDevices([]);
        setThingID('');
        setManufacturer('');
        setSerialNumber('');
        setLocation('');
    }

    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkDeviceSearch, setBulkDeviceSearch] = useState("");
    const searchModal =
        <Modal show={showBulkModal} onClose={() => setShowBulkModal(false)} disableOverlayClick>
            <ModalHeader>Bulk Device Search</ModalHeader>
            <ModalBody>
                <TextField multiline={true} rows={10} fullWidth
                    placeholder="Space or comma separated list of ThingIDs" 
                    onChange={(e) => setBulkDeviceSearch(e.target.value)}>
                </TextField>
            </ModalBody>
            <ModalFooter>
                <Button onClick={(e) => {
                    setShowBulkModal(false);
                    let newThingIDs = bulkDeviceSearch.split(/\s*[,\s]\s*/);
                    DeviceService.batchGetDevices(newThingIDs).then((ts) => {
                        setDevices(ts);
                    })
                }}>
                    Search
                </Button>
            </ModalFooter>
        </Modal>

    // Content
    const searchHeader: ReactNode = (
        <div className="devicesList_search">
            <ButtonGroup size='md'>

                <Button onClick={onSubmitSearchValues}>
                    <FontAwesomeIcon icon="redo"/>
                </Button>
                
                <Button onClick={() => setSearchOpen(!searchOpen)} ref={searchButtonRef}>
                    <FontAwesomeIcon icon="search"/>
                </Button>

                <Overlay
                    target={searchButtonRef}
                    show={searchOpen}
                    onHide={() => setSearchOpen(false)}
                    rootClose
                    placement="bottom"
                >
                    <Popover id="deviceSearch_popover" className="searchPopover">
                        <Popover.Content>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'Manufacturer'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <select value={manufacturer} onChange={onChangeManufacturer}>
                                            {ManufacturerService.allManufacturers.map((option) => (
                                                <option key={option.id} value={option.name}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </select>
                                        <button type="button" onClick={onSubmitSearchValues}>
                                            <FontAwesomeIcon icon="angle-right" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'Model Number'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <input
                                            type="text"
                                            value={modelNumber}
                                            onChange={onChangeModelNumber}
                                            onKeyPress={onKeypress}
                                            maxLength={128}
                                        />
                                        <button type="button" onClick={onSubmitSearchValues}>
                                            <FontAwesomeIcon icon="angle-right" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'Location'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <input
                                            type="text"
                                            value={location}
                                            onChange={onChangeLocation}
                                            onKeyPress={onKeypress}
                                            maxLength={128}
                                        />
                                        <button type="button" onClick={onSubmitSearchValues}>
                                            <FontAwesomeIcon icon="angle-right" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'Serial Number'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <input
                                            type="text"
                                            value={serialNumber}
                                            onChange={onChangeSerialNumber}
                                            onKeyPress={onKeypress}
                                            maxLength={128}
                                        />
                                        <button type="button" onClick={onSubmitSearchValues}>
                                            <FontAwesomeIcon icon="angle-right" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="searchPopover_group">
                                <div className="searchPopover_label">
                                    {'ThingID'}
                                </div>
                                <div className="searchPopover_value">
                                    <div className="inputWithButton">
                                        <input
                                            type="text"
                                            value={thingID}
                                            onChange={onChangeThingID}
                                            onKeyPress={onKeypress}
                                            maxLength={128}
                                        />
                                        <button type="button" onClick={onSubmitSearchValues}>
                                            <FontAwesomeIcon icon="angle-right" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Popover.Content>
                    </Popover>
                </Overlay>
            
                <Button onClick={() => setShowBulkModal(true)}>
                    <FontAwesomeIcon icon="search-plus"/>
                </Button>

                <Button onClick={clearDevices} disabled={devices.length == 0}>
                    <FontAwesomeIcon icon="window-close"/>
                </Button>

                {UserService.oktaUserInfo.role >= UserRole.Admin && 
                <Button onClick={() => openDeleteModal(selectedDevices)} disabled={selectedDevices.length == 0}>
                    <FontAwesomeIcon icon="trash" className="locationsList_trash" />
                </Button>}

            </ButtonGroup>
        </div>
    );

    const tableHeaders = (devices.length > 0 && (
        <div className="devicesHeaders">
            <div className="devicesHeaders_sorter devicesHeader">
                <input 
                    type="checkbox" 
                    className="devicesItem_check devicesHeaders_selectAll" 
                    onChange={onToggleSelectAll}
                    checked={allSelected}
                />
                <div className="devicesHeaders_selectAllText">
                    {'Select All'}
                </div>
            </div>
            <div className="devicesHeaders_sorter devicesHeader devicesHeaders_makeModel" onClick={sortDevicesByManufacturer}>
                {'Manufacturer'}
                {descending && activeSorter === "manufacturer" && (<FontAwesomeIcon icon="caret-down" className="devicesList_searchCaret" />)}
                {!descending && activeSorter === "manufacturer" && (<FontAwesomeIcon icon="caret-up" className="devicesList_searchCaret" />)}
            </div>
            <div className="devicesHeaders_sorter devicesHeader devicesHeaders_lastSeenDate" onClick={sortDevicesByLastSeen}>
                {'Last Seen'}
                {descending && activeSorter === "lastSeen" && (<FontAwesomeIcon icon="caret-down" className="devicesList_searchCaret" />)}
                {!descending && activeSorter === "lastSeen" && (<FontAwesomeIcon icon="caret-up" className="devicesList_searchCaret" />)}
            </div>
        </div>)
    );

    let content: ReactNode;
    switch (deviceLoadStatus) {
        case DeviceLoadState.Initial:
        case DeviceLoadState.Loading:
        case DeviceLoadState.Success:
            if (devices.length > 0 || deviceLoadStatus === DeviceLoadState.Loading) {
                content = (<>
                    <ul className="devicesList">
                        {devices.map((device) => (
                            <DevicesItem 
                                key={device.id}
                                device={device} 
                                isChecked={isSelected(device.id)}
                                className="devicesItem"
                                onShowDeleteModal={openDeleteModal} 
                                onShowDeviceModal={openDeviceModal} 
                                onSelectDevice={onSelectDevice}
                            />
                        ))}
                    </ul>
                    {deviceLoadStatus === DeviceLoadState.Loading && (
                        <Loader />
                    )}
                </>);
            } else {
                content = (
                    <div className="devicesList_empty">
                        {'No devices found.'}
                    </div>
                );
            }
            break;
        case DeviceLoadState.Error:
            content = (
                <ErrorMessage type={ErrorType.Generic} message={deviceLoadError || 'Error loading devices.'} />
            );
            break;
    }

    return (<>
        <Page>
            <PageHeader title={devices.length === 0 ? 'Devices' : 'Devices (' + devices.length + (devices.length == pageSize ? '+)' : ')')} right={searchHeader}/>
            {tableHeaders}
            {content}
        </Page>

        <Modal show={showDeviceModal} onClose={closeDeviceModal} size='lg' scrollMode='modal-body'>
            <DeviceModal
                key={0}
                device={selectedDevice}
                state={selectedState}
                refresh={refresh}
                onHide={closeDeviceModal}
            />
        </Modal>

        <Modal show={showDeleteModal} onClose={closeDeleteModal} size='md'>
            <DeleteModal
                key={0}
                devices={selectedDevices}
                refresh={refresh}
                onHide={closeDeleteModal}
            />
        </Modal>

        {searchModal}
    </>);
}
